import {
  ReactNode,
  forwardRef,
  MutableRefObject,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import cn from "classnames";
import Button from "@ebsco-ui/button";

import "./more-info.scss";

export interface MoreInfoProps {
  label?: string;
  className?: string;
  collapseButtonContent: ReactNode;
  expandButtonContent: ReactNode;
  lineLimit?: number;
  children?: ReactNode;
}

const MoreInfo = forwardRef<HTMLDivElement, MoreInfoProps>(
  (
    {
      label,
      className = "",
      collapseButtonContent,
      expandButtonContent,
      lineLimit = 7,
      children,
    }: MoreInfoProps,
    ref,
  ) => {
    const [isButtonVisible, setIsButtonVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const infoElementRef = useRef<HTMLDivElement>(null);
    const lineHeightElementRef = useRef<HTMLDivElement>(null);

    const infoElementRefCallback = useCallback(
      (node: HTMLDivElement) => {
        if (ref) {
          if (typeof ref === "function") {
            ref(node);
          } else {
            (ref as MutableRefObject<HTMLDivElement>).current = node;
          }
        }
        (infoElementRef as MutableRefObject<HTMLDivElement>).current = node;
      },
      [ref],
    );

    const handleClick = () => {
      setIsExpanded(!isExpanded);
    };

    const lineHeight = lineHeightElementRef.current
      ? lineHeightElementRef.current.offsetHeight
      : 0;

    const truncatedHeight = lineLimit * lineHeight;

    const checkShouldShowButton = useCallback(() => {
      const scrollheight = infoElementRef.current
        ? infoElementRef.current.scrollHeight
        : 0;

      const isTruncated = scrollheight > truncatedHeight + lineLimit;

      if (isTruncated && !isButtonVisible) {
        setIsButtonVisible(true);
        return;
      }
      if (!isTruncated && isButtonVisible) {
        setIsButtonVisible(false);
        setIsExpanded(false);
      }
    }, [infoElementRef, truncatedHeight, lineLimit, isButtonVisible]);

    useEffect(() => {
      checkShouldShowButton();
      window.addEventListener("resize", checkShouldShowButton);

      return () => window.removeEventListener("resize", checkShouldShowButton);
    }, [checkShouldShowButton]);

    const getBlocksWith = createBemBlockBuilder(["eb-more-info", className]);

    const infoClassNames = cn(
      getBlocksWith("__info"),
      getBlocksWith(`__info--${isExpanded ? "more" : "less"}`),
    );

    const infoElementHeight =
      isExpanded || !isButtonVisible
        ? { maxHeight: "none" }
        : { maxHeight: `${truncatedHeight}px` };

    return (
      <div className={getBlocksWith()} data-auto="more-info">
        {label && <label className={getBlocksWith("__label")}>{label}</label>}
        <div className={getBlocksWith("__info-wrapper")}>
          <div
            ref={infoElementRefCallback}
            style={infoElementHeight}
            className={infoClassNames}
            tabIndex={isButtonVisible ? 0 : -1}
            aria-label={label}
          >
            {children}
            <div
              aria-hidden="true"
              ref={lineHeightElementRef}
              className={getBlocksWith("__hidden-element-container")}
            >
              <span className={getBlocksWith("__hidden-element")}>
                {children}
              </span>
            </div>
          </div>
          {isButtonVisible && !isExpanded && (
            <hr
              className={getBlocksWith("__gradient")}
              data-auto="separator"
              aria-hidden="true"
            />
          )}
          {isButtonVisible && (
            <div className={getBlocksWith("__button-container")}>
              <Button
                className={getBlocksWith("__button")}
                tabIndex={0}
                onClick={handleClick}
                styleType="link"
              >
                {isExpanded ? collapseButtonContent : expandButtonContent}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

MoreInfo.displayName = "MoreInfo";

export default MoreInfo;
